import { Wallet, Users, CheckCircle2, Pencil, AlertTriangle, UserMinus, Mail } from "lucide-react";

export default function BetAndMembersCard({ pool, user, hasBet, stakeLabel, onEdit, togglingPaid, onTogglePaid, onRemoveMember, t }) {
    const members = pool.members || [];
    const sortedMembers = [...members].sort((a, b) => {
        if (a.id === pool.admin_id) return -1;
        if (b.id === pool.admin_id) return 1;
        const an = (a.name || a.email || "").toLowerCase();
        const bn = (b.name || b.email || "").toLowerCase();
        return an.localeCompare(bn);
    });
    const myPaid = pool.my_paid;
    const isAdmin = pool.is_admin;
    return (
        <div className="mb-6 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] overflow-hidden" data-testid="pool-bet-card">
            <div className="grid md:grid-cols-5 gap-0">
                {/* LEFT: Friendly bet info */}
                <div className="md:col-span-3 p-5 border-b md:border-b-0 md:border-r border-[var(--border-base)]">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Wallet className="h-5 w-5 text-[var(--accent-hex)]" />
                            <h3 className="font-heading text-base font-extrabold uppercase tracking-wide text-[var(--text-primary)]">{t("pool.bet.title")}</h3>
                        </div>
                        {isAdmin && (
                            <button onClick={onEdit} data-testid="pool-bet-edit"
                                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded border border-[var(--border-hover)] text-[var(--text-primary)] hover:border-[var(--accent-border)] hover:text-[var(--accent-hex)]">
                                <Pencil className="h-3 w-3" />
                                {t("pool.bet.edit")}
                            </button>
                        )}
                    </div>

                    {!hasBet && (
                        <p className="text-sm text-[var(--text-secondary)] italic">
                            {isAdmin ? t("pool.bet.none.admin") : "—"}
                        </p>
                    )}

                    {hasBet && (
                        <div className="space-y-3">
                            {stakeLabel && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-0.5">{t("pool.bet.stake")}</div>
                                    <div className="font-extrabold text-2xl text-[var(--accent-hex)]" data-testid="pool-bet-stake">{stakeLabel}</div>
                                </div>
                            )}
                            {pool.bet_winner && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-0.5">{t("pool.bet.winner")}</div>
                                    <div className="text-sm text-[var(--text-primary)]" data-testid="pool-bet-winner">{pool.bet_winner}</div>
                                </div>
                            )}
                            {pool.bet_instructions && (
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-0.5">{t("pool.bet.instructions")}</div>
                                    <div className="text-sm text-[var(--text-primary)] whitespace-pre-wrap" data-testid="pool-bet-instructions">{pool.bet_instructions}</div>
                                </div>
                            )}

                            {/* My status reminder */}
                            <div className={`mt-3 flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
                                myPaid
                                    ? "border-[var(--accent-border)] bg-[var(--accent-hex)]/10 text-[var(--accent-hex)]"
                                    : "border-amber-500/40 bg-amber-500/10 text-amber-400"
                            }`} data-testid="pool-bet-my-status">
                                {myPaid ? <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
                                <div>
                                    <div className="font-bold">{myPaid ? t("pool.bet.my_paid") : t("pool.bet.my_unpaid")}</div>
                                    {!myPaid && <div className="text-xs opacity-90 mt-0.5">{t("pool.bet.my_unpaid_hint")}</div>}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT: Members & paid tracking */}
                <div className="md:col-span-2 p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Users className="h-5 w-5 text-[var(--accent-hex)]" />
                        <h3 className="font-heading text-base font-extrabold uppercase tracking-wide text-[var(--text-primary)]">{t("pool.members.title")}</h3>
                    </div>
                    <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                        <p className="text-xs text-[var(--text-secondary)]">
                            {isAdmin ? t("pool.members.hint_admin") : t("pool.members.hint_member")}
                        </p>
                        {(() => {
                            // Build a `mailto:` link to every member who has an email AND
                            // isn't the current user (no point emailing yourself).
                            const recipients = (sortedMembers || [])
                                .filter((m) => m.id !== user?.id && m.email)
                                .map((m) => m.email)
                                .join(",");
                            if (!recipients) return null;
                            return (
                                <a
                                    href={`mailto:${recipients}`}
                                    data-testid="pool-members-email-all"
                                    title={t("pool.members.email_all")}
                                    className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest px-2.5 py-1 rounded border border-amber-500/50 text-amber-400 hover:border-amber-500 hover:text-amber-300 transition-colors"
                                >
                                    <Mail className="h-3 w-3" /> {t("pool.members.email_all")}
                                </a>
                            );
                        })()}
                    </div>
                    <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="pool-members-list">
                        {sortedMembers.map((m) => {
                            const isMe = m.id === user?.id;
                            const isMemberAdmin = m.id === pool.admin_id;
                            const paid = !!m.paid;
                            const busy = !!togglingPaid[m.id];
                            const name = m.name || m.email || "—";
                            const canToggle = isAdmin && !busy;
                            return (
                                <li key={m.id} className="flex items-center justify-between gap-2 rounded-md border border-[var(--border-base)] bg-[var(--bg-base)] px-3 py-2"
                                    data-testid={`pool-member-row-${m.id}`}>
                                    <div className="flex items-center gap-2 min-w-0">
                                        <button
                                            type="button"
                                            disabled={!canToggle}
                                            onClick={() => canToggle && onTogglePaid(m.id, paid, name)}
                                            className={`flex-shrink-0 h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
                                                paid
                                                    ? "bg-[var(--accent-hex)] border-[var(--accent-hex)]"
                                                    : "border-[var(--border-hover)] hover:border-[var(--accent-hex)]"
                                            } ${canToggle ? "cursor-pointer" : "cursor-default"}`}
                                            data-testid={`pool-member-paid-toggle-${m.id}`}
                                            aria-label={paid ? t("pool.members.paid") : t("pool.members.unpaid")}
                                            title={isAdmin ? (paid ? t("pool.members.paid") : t("pool.members.unpaid")) : ""}
                                        >
                                            {paid && <CheckCircle2 className="h-3.5 w-3.5 text-[var(--accent-text)]" />}
                                        </button>
                                        <div className="min-w-0" title={m.email || ""}>
                                            <div className="text-sm font-semibold text-[var(--text-primary)] truncate" data-testid={`pool-member-name-${m.id}`}>
                                                {name}
                                                {isMe && <span className="ml-1.5 text-[10px] uppercase tracking-widest text-[var(--text-muted)]">({t("pool.members.you")})</span>}
                                                {isMemberAdmin && <span className="ml-1.5 text-[10px] uppercase tracking-widest font-bold text-[var(--accent-hex)]">{t("pool.members.admin_tag")}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0">
                                        {m.email && !isMe && (
                                            <a
                                                href={`mailto:${m.email}`}
                                                title={t("pool.members.email_btn")}
                                                aria-label={t("pool.members.email_btn")}
                                                data-testid={`pool-member-email-${m.id}`}
                                                className="p-1.5 rounded text-[var(--text-secondary)] hover:text-[var(--accent-hex)] hover:bg-[var(--overlay-light)] transition-colors"
                                            >
                                                <Mail className="h-3.5 w-3.5" />
                                            </a>
                                        )}
                                        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${
                                            paid
                                                ? "bg-[var(--accent-hex)]/15 text-[var(--accent-hex)]"
                                                : "bg-amber-500/15 text-amber-400"
                                        }`}>
                                            {paid ? t("pool.members.paid") : t("pool.members.unpaid")}
                                        </span>
                                        {isAdmin && !isMemberAdmin && onRemoveMember && (
                                            <button type="button" onClick={() => onRemoveMember(m.id, name)}
                                                data-testid={`pool-member-remove-${m.id}`}
                                                title={t("pool.members.remove.button")}
                                                className="p-1.5 rounded text-[var(--text-muted)] hover:text-red-500 hover:bg-red-500/10 transition-colors">
                                                <UserMinus className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </div>
    );
}
